import ActionRow from '@/components/ActionRow';
import DetailsCard from '@/components/DetailsCard';
import DndContainer from '@/components/DndContainer';
import { ActionList, DroppableContainer } from '@/components/DragAndDrop';
import ActionListItem from '@/components/DragAndDrop/ActionListItem';
import EmptyActionList from '@/components/EmptyActionList';
import QuickActionCreator from '@/components/QuickActionCreator';
import { CATEGORIES_ICONS } from '@/constants/category';
import useIsDarkMode from '@/hooks/useIsDarkMode';
import useIsDailyPlanPage from '@/services/plans/hooks/useIsDailyPlanPage';
import { RoutesList } from '@/routes/routesList';
import { useUpdateAction } from '@/services/action/hooks';
import { useDeleteBlock, useUpdateActionsCategoryOrder } from '@/services/myPlan/hooks';
import { useActionsCategories } from '@/stores/useActionsCategories';
import { useCalendarMonthlyStore } from '@/stores/useCalendar';
import { IconCalendar, IconFire } from '@/theme/icons';
import { Action, ActionResponse } from '@/types/actions';
import { Category } from '@/types/category';
import { getActionById, getActionsByBlockId, getActionsByCategoryId } from '@/utils/action';
import { endOfTheWeek, startOfTheWeek } from '@/utils/calendar';
import { getColorFromToken } from '@/utils/color';
import { isUncategorized } from '@/utils/index';
import rem from '@/utils/rem';
import { Flex, HStack, Icon, Link, Spinner, Text, VStack, useToast } from '@chakra-ui/react';
import { DragEndEvent, DragOverlay, DragStartEvent, UniqueIdentifier } from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import { useAnimation } from 'framer-motion';
import { isEmpty } from 'lodash';
import { memo, useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { NavLink as RouterLink } from 'react-router-dom';

export type CategoryWithActions = {
  completedActions: Action[];
  incompleteActions: Action[];
} & Category;

type Props = {
  category: CategoryWithActions | undefined;
  actions: ActionResponse | undefined;
  isCategoriesLoading: boolean;
  isLoadingActions: boolean;
};

function RightSection({ category, actions, isCategoriesLoading, isLoadingActions }: Props) {
  const { t } = useTranslation('WeeklyCapturePage');
  const toast = useToast();
  const actionCreatorOptionsAnimation = useAnimation();
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const isDarkMode = useIsDarkMode();

  const selectedCategory = useActionsCategories((state) => state.selectedCategory);
  const updateActionsCategoryOrder = useUpdateActionsCategoryOrder();

  const deleteBlock = useDeleteBlock();

  const selectedDate = useCalendarMonthlyStore((state) => state.selectedDate);
  const isDailyPlanPage = useIsDailyPlanPage();
  const recurringWeekContext = useMemo(
    () =>
      isDailyPlanPage
        ? null
        : {
            weekStart: startOfTheWeek(selectedDate),
            weekEnd: endOfTheWeek(selectedDate),
          },
    [isDailyPlanPage, selectedDate],
  );

  const ItemOverlay = useMemo(() => {
    if (!activeId || !actions?.action) {
      return <></>;
    }

    const item = getActionById(actions.action, activeId.toString());

    if (!item) {
      return <></>;
    }

    return createPortal(
      <DragOverlay dropAnimation={{ easing: 'ease-in' }}>
        <ActionRow
          action={item}
          idx={activeItemIndex + 1}
          disableRecurringStatusChanges
          recurringWeekContext={recurringWeekContext}
        />
      </DragOverlay>,
      document.body,
    );
  }, [actions?.action, activeId, activeItemIndex, recurringWeekContext]);

  const SingleCategoryContainer = useCallback(
    (category: CategoryWithActions) => {
      if (!category) return <></>;

      if (isLoadingActions) {
        return (
          <Flex justifyContent="center" width="full">
            <Spinner />
          </Flex>
        );
      }

      return (
        <DroppableContainer
          id={category?.id}
          key={category?.id}
          payload={{
            categoryId: category?.id,
          }}
          as={VStack}
          alignItems="flex-start"
          width="full"
          borderRadius={rem(16)}
        >
          <SortableContext items={category?.incompleteActions}>
            <ActionList
              width="full"
              emptyListComponent={
                <DroppableContainer
                  id="captureList"
                  marginTop={rem(12)}
                  payload={{ containerId: 'captureList', category }}
                >
                  <EmptyActionList message={t('emptyStates.noIncompleteActions')} />
                </DroppableContainer>
              }
              isEmpty={isEmpty(category?.incompleteActions)}
            >
              {category?.incompleteActions.map((action, index) => (
                <ActionListItem
                  key={action.id}
                  item={action}
                  index={index}
                  dndPayload={{
                    containerId: `captureList-${category?.id}`,
                    categoryId: action.categoryId,
                    category,
                    index,
                    projectId: action?.projectId,
                    // blockId: action?.blockId,
                  }}
                  disableRecurringStatusChanges
                  recurringWeekContext={recurringWeekContext}
                />
              ))}
            </ActionList>
          </SortableContext>
        </DroppableContainer>
      );
    },
    [isLoadingActions, t, recurringWeekContext],
  );

  // Drag and Drop methods
  const updateCaptureListActionsOrder = useCallback(
    (actions: Action[]) => {
      const mutationPayload = actions.map((action, index) => ({
        _set: {
          categoryOrder: index + 1,
        },
        where: {
          id: {
            _eq: action.id,
          },
        },
      }));

      updateActionsCategoryOrder.mutate(mutationPayload);
    },
    [updateActionsCategoryOrder],
  );

  const updateActionCategory = useUpdateAction({
    onError: (error): void => {
      toast({
        title: error?.response?.errors?.[0]?.message || t('toasts.error'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const handleActionNewCategory = useCallback(
    (actionId: string, categoryId: string, blockId: string | null) => {
      const actionsByCategory = getActionsByCategoryId(actions?.action ?? [], categoryId);

      let shouldDeleteBlock = false;

      if (blockId) {
        const blockActions = getActionsByBlockId(blockId, actions?.action ?? []);

        if (blockActions?.length === 1) {
          shouldDeleteBlock = true;
        }
      }

      updateActionCategory.mutate({
        actionId,
        set: { categoryId, categoryOrder: actionsByCategory.length + 1, blockId: null, blockOrder: null },
        showSyncEvent: false,
      });

      if (shouldDeleteBlock && blockId) {
        deleteBlock.mutate({ blockId });
      }
    },
    [actions?.action, deleteBlock, updateActionCategory],
  );

  const onDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id);
    setActiveItemIndex(active?.data?.current?.index);
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    const overId = over?.id;

    if (overId == null) {
      setActiveId(null);
      setActiveItemIndex(0);
      return;
    }

    const activeIndex = active?.data?.current?.index;
    const overIndex = over?.data?.current?.index;

    const activeContainerId = active?.data?.current?.containerId;
    const overContainerId = over?.data?.current?.containerId;

    const activeCategoryId = active?.data?.current?.categoryId;
    const overCategoryId = over?.data?.current?.categoryId;

    const activeBlockId = active?.data?.current?.blockId;

    const activeProjectId = active?.data?.current?.projectId;
    const overProjectId = over?.data?.current?.projectId;

    const notNullProjectId = ![null, undefined].includes(activeProjectId, overProjectId);

    if (notNullProjectId && activeProjectId !== overProjectId && activeCategoryId !== overCategoryId) {
      setActiveItemIndex(0);

      return;
    }

    if (activeContainerId === overContainerId && category?.incompleteActions?.length) {
      const reordered = arrayMove(category.incompleteActions, activeIndex ?? 0, overIndex ?? 0);
      updateCaptureListActionsOrder(reordered);
    }

    if (activeContainerId !== overContainerId) {
      handleActionNewCategory(active.id.toString(), overCategoryId, activeBlockId);
    }

    setActiveId(null);
    setActiveItemIndex(0);
  };

  const onDragCancel = () => {
    setActiveId(null);
    setActiveItemIndex(0);
  };
  // / Drag and Drop methods

  if (isCategoriesLoading && !category) {
    return (
      <Flex justifyContent="center" width="full">
        <Spinner />
      </Flex>
    );
  }

  return (
    <>
      {!isUncategorized(category) && category?.ultimatePurpose && (
        <DetailsCard
          paddingX={rem(24)}
          paddingTop={rem(24)}
          paddingBottom={rem(32)}
          color={isDarkMode ? category?.color : getColorFromToken(category?.color, undefined, '100')}
          icon={IconFire}
          sectionTitle={t('sections.rememberYourUltimatePurpose')}
          height="fit-content"
          border="1px solid rgba(67, 78, 97, 0.30)"
          opacized
        >
          <Text as="p" textStyle="h4SatBlack" color="text-primary">
            {category.ultimatePurpose}
          </Text>
        </DetailsCard>
      )}

      <DetailsCard
        paddingX={rem(24)}
        paddingTop={rem(24)}
        paddingBottom={rem(32)}
        color={isDarkMode ? category?.color : getColorFromToken(category?.color, undefined, '100')}
        icon={CATEGORIES_ICONS?.[category?.icon ?? 'uncategorized']}
        sectionTitle={t('sections.thisWeeksActionsAndOutcomes')}
        rightButton={
          <Link
            as={RouterLink}
            padding={rem(10)}
            border="none"
            borderRadius={4}
            _hover={{
              textDecoration: 'none',
            }}
            backgroundColor="background-primary"
            to={RoutesList.MyPlanPage}
          >
            <HStack width="auto">
              <Icon as={IconCalendar} boxSize={rem(19)} strokeWidth={2} />

              <Text textStyle="smallBlack" width="max-content" color="text-primary">
                {t('buttons.planMyWeek')}
              </Text>
            </HStack>
          </Link>
        }
        height="fit-content"
        border="1px solid rgba(67, 78, 97, 0.30)"
        opacized
      >
        <QuickActionCreator
          category={category}
          selectedCategoryId={selectedCategory?.id}
          actionCreatorOptionsAnimation={actionCreatorOptionsAnimation}
          fancyCapturePage
        />

        <DndContainer onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
          {SingleCategoryContainer(category as CategoryWithActions)}

          {ItemOverlay}
        </DndContainer>
      </DetailsCard>
    </>
  );
}

export default memo(RightSection);
